"use client"

/**
 * FAQ ?q= URL synchronization — must be rendered inside <Suspense>.
 *
 * Why this is its own file: `useSearchParams()` is a dynamic-only API
 * under Next 16 cacheComponents. Calling it directly from `FaqClient`
 * (a Client Component rendered straight from a `"use cache"` page)
 * walks the bailout up to the root layout's `<Suspense fallback={null}>`
 * and bakes an empty `<main>` (`<!--$--><!--/$-->` empty-Suspense
 * signature) into the static prerender — observed live as
 * `nav=1 footer=1 h1=0 main=1 bailout=2` on /faq, breaking
 * `pre-release: SSR content for bots` (see AHD-6235 / reverted PR #1035).
 *
 * Localizing the bailout: this component renders nothing (`return null`)
 * but uses `useSearchParams` / `useRouter` / `usePathname`. Wrap it in
 * `<Suspense fallback={null}>` from `FaqClient`. The Suspense boundary
 * scopes the dynamic bailout to this subtree only — the FAQ list
 * itself stays in the static shell, so SEO + bot crawlers still see
 * full content.
 *
 * Behavior:
 * - On mount, reads `?q=` and calls `onHydrate(query)` once if non-empty.
 * - When `searchQuery` (parent state) changes, writes/removes `?q=`
 *   via `router.replace` with `scroll: false` so the page doesn't jump.
 *
 * Reference for the same trap: `/animation-cache-trap` skill,
 * `app/(frontend)/[country]/[locale]/(default-nav)/search/page.tsx`.
 */

import { useEffect, useRef } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

export function FaqUrlSync({
  searchQuery,
  onHydrate,
}: {
  searchQuery: string
  onHydrate: (q: string) => void
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hydratedRef = useRef(false)

  // One-shot hydration from URL on first render. We read once and notify
  // the parent so it can seed `searchQuery`. Subsequent URL changes from
  // the writer effect below should NOT re-trigger hydration.
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true
    const initial = searchParams.get("q") ?? ""
    if (initial) onHydrate(initial)
  }, [searchParams, onHydrate])

  // Write search query → URL. `scroll: false` keeps the user's scroll
  // position; `router.replace` (not `push`) so back-button still works.
  useEffect(() => {
    if (!hydratedRef.current) return
    const params = new URLSearchParams(searchParams.toString())
    const trimmed = searchQuery.trim()
    const current = params.get("q") ?? ""
    if (trimmed === current) return
    if (trimmed) {
      params.set("q", trimmed)
    } else {
      params.delete("q")
    }
    const qs = params.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
  }, [searchQuery, pathname, router, searchParams])

  return null
}
